<template>
  <el-card class="comment-card shadow-box">
    <div class="comment-container">
      <!-- 头部：统计与排序 -->
      <div class="comment-header">
        <span class="total-count">{{ totalSize }} 评论</span>
        <div class="sort-options">
          <span :class="{ active: sortType === 'hot' }" @click="handleSort('hot')">最热</span>
          <el-divider direction="vertical" />
          <span :class="{ active: sortType === 'time' }" @click="handleSort('time')">最新</span>
        </div>
      </div>

      <!-- 主发布框 -->
      <div class="main-reply-box">
        <el-avatar
          :size="48"
          :src="currentUser.avatar || defaultAvatar"
          class="clickable-avatar"
          @click.native="goToUserHome(currentUser.userId)"
        />
        <div class="reply-input-wrapper">
          <el-input
            v-model="newComment"
            type="textarea"
            :rows="3"
            placeholder="发一条友善的评论吧"
            resize="none"
          />
          <el-button type="primary" class="post-btn" @click="postMainComment">发表评论</el-button>
        </div>
      </div>

      <!-- 评论列表 -->
      <div
        v-infinite-scroll="loadMoreMainComments"
        class="comment-list"
        :infinite-scroll-disabled="mainScrollDisabled"
        :infinite-scroll-distance="20"
      >
        <div v-for="item in dataList" :key="item.id" class="comment-item">
          <div class="comment-root">
            <el-avatar
              :size="48"
              :src="item.replyUser.avatar || defaultAvatar"
              class="clickable-avatar"
              @click.native="goToUserHome(item.replyUser.userId)"
            />
            <div class="comment-content">
              <div class="user-info">
                <span class="user-name clickable-link" @click="goToUserHome(item.replyUser.userId)">
                  {{ item.replyUser.name }}
                </span>
                <span v-if="item.location" class="location">{{ item.location }}</span>
              </div>

              <!-- 主评论内容 -->
              <div class="text rich-content" v-html="item.content" />

              <!-- 底部操作栏 -->
              <div class="comment-footer">
                <span class="time">{{ formatDate(item.createAt) }}</span>
                <span class="action-btn" @click="handleLike(item)">
                  👍 {{ item.likeCount || '点赞' }}
                </span>
                <span class="action-btn" @click="showReplyInput(item, item)">回复</span>
              </div>

              <!-- 子评论预览区（最多展示热门前3条） -->
              <div v-if="item.hotChildReplies && item.hotChildReplies.length > 0" class="reply-list">
                <div v-for="reply in item.hotChildReplies" :key="reply.id" class="reply-item">
                  <el-avatar
                    :size="24"
                    :src="reply.replyUser.avatar || defaultAvatar"
                    class="reply-avatar clickable-avatar"
                    @click.native="goToUserHome(reply.replyUser.userId)"
                  />
                  <div class="reply-right">
                    <span class="user-name clickable-link" @click="goToUserHome(reply.replyUser.userId)">
                      {{ reply.replyUser.name }}
                    </span>

                    <!-- 判断 rootId !== parentId 时，插入 回复 @targetUserName 标识 -->
                    <template v-if="reply.rootId !== reply.parentId && reply.targetUserName">
                      <span class="reply-text-label">回复</span>
                      <span class="target-user clickable-link" @click="goToUserHome(reply.targetUserId)">
                        @{{ reply.targetUserName }}
                      </span>
                      <span class="colon">:</span>
                    </template>
                    <template v-else>
                      <span class="colon">:</span>
                    </template>

                    <!-- 子评论内容 -->
                    <span class="reply-content rich-content" v-html="reply.content" />

                    <div class="sub-footer">
                      <span class="time">{{ formatDate(reply.createAt) }}</span>
                      <span v-if="reply.location" class="location">{{ reply.location }}</span>
                      <span class="sub-action" @click="handleLike(reply)">👍 {{ reply.likeCount || '' }}</span>
                      <span class="sub-action" @click="showReplyInput(item, reply)">回复</span>
                    </div>
                  </div>
                </div>

                <!-- 查看更多子评论链接 -->
                <div v-if="item.childCount > item.hotChildReplies.length" class="view-more-bar">
                  共 {{ item.childCount }} 条回复，
                  <span class="click-more" @click="openReplyDialog(item)">点击查看更多</span>
                </div>
              </div>

              <!-- 行内内嵌回复框 -->
              <div v-if="activeRootId === item.id && !dialogVisible" class="inner-reply-box">
                <el-input v-model="replyText" size="small" :placeholder="replyPlaceholder" />
                <el-button type="primary" size="small" @click="postReply">发布</el-button>
                <el-button type="text" size="small" @click="cancelReply">取消</el-button>
              </div>
            </div>
          </div>
          <el-divider />
        </div>

        <p v-if="loading" class="list-status">加载中...</p>
        <p v-if="noMoreMain && dataList.length > 0" class="list-status">没有更多评论了</p>
        <el-empty v-if="!loading && dataList.length === 0" description="暂无评论，快来抢沙发" />
      </div>

      <!-- 评论详情弹窗 -->
      <el-dialog
        :visible.sync="dialogVisible"
        title="评论详情"
        width="600px"
        :append-to-body="true"
        custom-class="reply-dialog"
        @close="handleDialogClose"
      >
        <div
          v-if="currentParent"
          v-infinite-scroll="loadMoreChildComments"
          class="dialog-scroll-area"
          :infinite-scroll-disabled="childScrollDisabled"
          :infinite-scroll-distance="20"
        >
          <!-- 根评论详情 -->
          <div class="parent-info">
            <el-avatar
              :size="40"
              :src="currentParent.replyUser.avatar || defaultAvatar"
              class="clickable-avatar"
              @click.native="goToUserHome(currentParent.replyUser.userId)"
            />
            <div class="parent-text">
              <div class="user-name clickable-link" @click="goToUserHome(currentParent.replyUser.userId)">
                {{ currentParent.replyUser.name }}
              </div>

              <div class="content rich-content" v-html="currentParent.content" />

              <div class="reply-footer">
                <span class="time">{{ formatDate(currentParent.createAt) }}</span>
                <span v-if="currentParent.location" class="location">{{ currentParent.location }}</span>
                <span class="action" @click="showReplyInput(currentParent, currentParent)">回复</span>
              </div>
            </div>
          </div>

          <el-divider content-position="left">全部回复 ({{ currentParent.childCount }})</el-divider>

          <!-- 弹窗子评论列表 -->
          <div class="dialog-reply-list">
            <div v-for="sub in dialogReplies" :key="sub.id" class="dialog-reply-item">
              <el-avatar
                :size="32"
                :src="sub.replyUser.avatar || defaultAvatar"
                class="clickable-avatar"
                @click.native="goToUserHome(sub.replyUser.userId)"
              />
              <div class="reply-body">
                <span class="user-name clickable-link" @click="goToUserHome(sub.replyUser.userId)">
                  {{ sub.replyUser.name }}
                </span>

                <!-- 弹窗内判断 rootId !== parentId 时，插入 回复 @targetUserName 标识 -->
                <template v-if="sub.rootId !== sub.parentId && sub.targetUserName">
                  <span class="reply-text-label">回复</span>
                  <span class="target-user clickable-link" @click="goToUserHome(sub.targetUserId)">
                    @{{ sub.targetUserName }}
                  </span>
                  <span class="colon">:</span>
                </template>
                <template v-else>
                  <span class="colon">:</span>
                </template>

                <span class="reply-content rich-content" v-html="sub.content" />

                <div class="reply-footer">
                  <span class="time">{{ formatDate(sub.createAt) }}</span>
                  <span v-if="sub.location" class="location">{{ sub.location }}</span>
                  <span class="action" @click="showReplyInput(currentParent, sub)">回复</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 加载状态提示 -->
          <p v-if="childLoading" class="list-status">加载中...</p>
          <p v-if="childNoMore && dialogReplies.length > 0" class="list-status">已加载全部回复</p>
        </div>

        <!-- 弹窗底部回复区域 -->
        <div v-if="dialogVisible && activeRootId === currentParent.id" class="dialog-inner-reply">
          <el-divider />
          <div class="inner-reply-box">
            <el-input v-model="replyText" size="small" :placeholder="replyPlaceholder" />
            <el-button type="primary" size="small" @click="postReply">发布</el-button>
            <el-button type="text" size="small" @click="cancelReply">取消</el-button>
          </div>
        </div>
      </el-dialog>
    </div>
  </el-card>
</template>

<script>
import { getChildComment, getComment, publishComment } from '@/api/vod'

export default {
  name: 'UserCommentCard',
  props: {
    postId: { type: String, required: true },
    postType: { type: [String, Number], default: '' },
    currentUser: { type: Object, default: () => ({ userId: '-1', name: '游客', avatar: '' }) }
  },
  data() {
    return {
      // 主评论列表相关
      dataList: [],
      totalSize: 0,
      currentPage: 0,
      pageSize: 10,
      loading: false,
      noMoreMain: false,
      sortType: 'hot',

      // 弹窗与子评论相关
      dialogVisible: false,
      currentParent: null,
      dialogReplies: [],
      childCurrentPage: 0,
      childPageSize: 10,
      childLoading: false,
      childNoMore: false,

      // 发表与回复相关
      newComment: '',
      replyText: '',
      activeRootId: null,
      replyTarget: null,
      replyPlaceholder: '',
      defaultAvatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
    }
  },
  computed: {
    mainScrollDisabled() {
      return this.loading || this.noMoreMain
    },
    childScrollDisabled() {
      return this.childLoading || this.childNoMore
    }
  },
  watch: {
    postId: {
      immediate: true,
      handler(val) {
        if (val) this.initCommentList()
      }
    }
  },
  methods: {
    goToUserHome(userId) {
      if (!userId || userId === '0') return
      this.$message.info('跳转到用户主页: ' + userId)
    },
    initCommentList() {
      this.dataList = []
      this.currentPage = 0
      this.noMoreMain = false
      this.loadMoreMainComments()
    },
    async loadMoreMainComments() {
      if (this.loading || this.noMoreMain) return
      this.loading = true
      this.currentPage++
      try {
        const resp = await getComment({
          postId: this.postId,
          postType: this.postType,
          pn: this.currentPage,
          pageSize: this.pageSize,
          sortBy: this.sortType
        })

        // 判断响应是否成功 (匹配业务 Code 0 或 HTTP 200)
        if (resp && resp.code === 0 && resp.data) {
          const newList = resp.data.list || []
          this.dataList = [...this.dataList, ...newList]
          this.totalSize = resp.data.totalSize || 0

          if (!resp.data.hasNext || this.dataList.length >= this.totalSize) {
            this.noMoreMain = true
          }
        } else {
          // 接口业务错误时，标记 noMoreMain 停止后续自动触底刷新
          this.noMoreMain = true
          this.$message.error(resp?.msg || '获取评论列表失败')
        }
      } catch (e) {
        // 捕获网络错误/HTTP 非200响应，停止后续滚动请求
        this.noMoreMain = true
        console.error('加载评论列表异常', e)
      } finally {
        this.loading = false
      }
    },
    openReplyDialog(parentItem) {
      this.currentParent = parentItem
      this.dialogReplies = []
      this.childCurrentPage = 0
      this.childNoMore = false
      this.dialogVisible = true
      this.cancelReply()
    },
    async loadMoreChildComments() {
      if (this.childLoading || this.childNoMore || !this.currentParent) return
      this.childLoading = true
      this.childCurrentPage++
      try {
        const resp = await getChildComment({
          commentId: this.currentParent.id,
          postId: this.postId,
          postType: this.postType,
          pn: this.childCurrentPage,
          pageSize: this.childPageSize
        })

        // 判断响应是否成功
        if (resp && resp.code === 0 && resp.data) {
          const newList = resp.data.list || []
          this.dialogReplies = [...this.dialogReplies, ...newList]

          if (!resp.data.hasNext || this.dialogReplies.length >= (resp.data.totalSize || 0)) {
            this.childNoMore = true
          }
        } else {
          // 接口业务错误时，阻断后续滚动请求
          this.childNoMore = true
          this.$message.error(resp?.msg || '获取回复列表失败')
        }
      } catch (e) {
        // 捕获网络错误/HTTP 非200响应，停止后续请求
        this.childNoMore = true
        console.error('加载子评论异常', e)
      } finally {
        this.childLoading = false
      }
    },
    handleDialogClose() {
      this.cancelReply()
      this.currentParent = null
      this.dialogReplies = []
    },
    handleSort(type) {
      if (this.sortType === type) return
      this.sortType = type
      this.initCommentList()
    },
    handleLike(comment) {
      comment.likeCount = (comment.likeCount || 0) + 1
      this.$message.success('点赞成功')
    },
    showReplyInput(root, target) {
      this.activeRootId = root.id
      this.replyTarget = target
      this.replyPlaceholder = `回复 @${target.replyUser.name} :`
      this.replyText = ''
    },
    cancelReply() {
      this.activeRootId = null
      this.replyTarget = null
      this.replyText = ''
    },
    postMainComment() {
      if (!this.newComment.trim()) return
      publishComment({
        postId: this.postId,
        postType: this.postType,
        content: this.newComment,
        rootId: 0,
        parentId: 0
      }).then(resp => {
        if (resp && resp.code === 0) {
          this.$message.success('评论成功')
          this.newComment = ''
          this.initCommentList()
        } else {
          this.$message.error(resp?.msg || '发表评论失败')
        }
      }).catch(err => {
        console.error('发表评论失败', err)
      })
    },
    postReply() {
      if (!this.replyText.trim()) return

      publishComment({
        postId: this.postId,
        postType: this.postType,
        content: this.replyText,
        rootId: this.activeRootId,
        parentId: this.replyTarget.id
      }).then(resp => {
        if (resp && resp.code === 0) {
          this.$message.success('回复成功')
          const isDialog = this.dialogVisible
          this.cancelReply()

          if (isDialog) {
            this.openReplyDialog(this.currentParent)
          } else {
            this.initCommentList()
          }
        } else {
          this.$message.error(resp?.msg || '回复失败')
        }
      }).catch(err => {
        console.error('回复失败', err)
      })
    },
    formatDate(ts) {
      if (!ts) return ''
      const date = new Date(ts)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped lang="scss">
.comment-card { margin-top: 20px; border: none; }
.comment-container { color: #18191c; }

.clickable-avatar {
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.8; }
}
.clickable-link {
  cursor: pointer;
  &:hover { color: #00aeec !important; }
}

/* 富文本内容基础样式兼容 */
::v-deep .rich-content {
  word-break: break-word;
  line-height: 1.6;

  img {
    vertical-align: text-bottom;
    max-width: 100%;
    height: auto;
    max-height: 24px;
    margin: 0 2px;
  }

  p {
    margin: 4px 0;
    display: inline; // 让子评论的富文本能与前面的“回复 @xxx”保持同行显示
  }
}

.comment-header {
  margin-bottom: 24px; display: flex; align-items: center;
  .total-count { font-size: 18px; font-weight: 500; margin-right: 20px; }
  .sort-options span {
    font-size: 14px; color: #9499a0; cursor: pointer;
    &.active { color: #18191c; font-weight: bold; }
  }
}

.main-reply-box {
  display: flex; gap: 15px; margin-bottom: 30px;
  .reply-input-wrapper { flex: 1; .post-btn { margin-top: 10px; float: right; } }
}

.comment-list { min-height: 200px; }
.list-status { text-align: center; color: #9499a0; font-size: 13px; padding: 15px 0; }

.comment-root {
  display: flex; gap: 15px;
  .comment-content {
    flex: 1;
    .user-info {
      display: flex; align-items: center; gap: 8px;
      .user-name { font-size: 13px; font-weight: bold; color: #61666d; }
      .location { font-size: 12px; color: #9499a0; }
    }
    .text { font-size: 15px; margin: 8px 0; }
    .comment-footer {
      font-size: 13px; color: #9499a0; display: flex; gap: 20px; margin-bottom: 8px;
      .action-btn { cursor: pointer; &:hover { color: #00aeec; } }
    }
  }
}

.reply-list {
  background: #f6f7f8; border-radius: 6px; padding: 12px; margin-top: 8px;
  .reply-item {
    display: flex; gap: 8px; font-size: 14px; line-height: 22px; margin-bottom: 10px;
    .reply-avatar { flex-shrink: 0; margin-top: 2px; }
    .reply-right {
      flex: 1;
      /* 让评论文本、@用户与冒号保持行内自然连贯 */
      line-height: 22px;
    }
    .user-name { color: #61666d; font-weight: bold; margin-right: 4px; }
    .reply-text-label { margin: 0 4px; color: #222; }
    .target-user { color: #00aeec; margin-right: 2px; }
    .colon { margin-right: 4px; }
    .reply-content {
      color: #18191c;
      display: inline;
    }
    .sub-footer {
      font-size: 12px; color: #9499a0; display: flex; gap: 12px; margin-top: 2px;
      .sub-action { cursor: pointer; &:hover { color: #00aeec; } }
    }
  }
  .view-more-bar {
    font-size: 13px; color: #9499a0; margin-top: 8px; padding-left: 32px;
    .click-more { color: #00aeec; cursor: pointer; font-weight: 500; }
  }
}

.inner-reply-box {
  margin-top: 10px; display: flex; gap: 8px;
  background: #fff; padding: 10px; border: 1px solid #e3e5e7; border-radius: 6px;
}

.dialog-scroll-area {
  max-height: 55vh;
  overflow-y: auto;
  padding-right: 8px;
  .parent-info {
    display: flex; gap: 12px;
    .parent-text {
      flex: 1;
      .user-name { font-weight: bold; color: #61666d; margin-bottom: 4px; }
      .content { font-size: 15px; margin-bottom: 8px; }
      .reply-footer {
        font-size: 12px; color: #9499a0; display: flex; gap: 12px;
        .action { cursor: pointer; &:hover { color: #00aeec; } }
      }
    }
  }
  .dialog-reply-item {
    display: flex; gap: 10px; margin-bottom: 15px;
    .reply-body {
      flex: 1; font-size: 14px; line-height: 22px;
      .user-name { font-weight: bold; color: #61666d; margin-right: 4px; }
      .reply-text-label { margin: 0 4px; color: #222; }
      .target-user { color: #00aeec; margin-right: 2px; }
      .colon { margin-right: 4px; }
      .reply-footer {
        font-size: 12px; color: #9499a0; margin-top: 5px; display: flex; gap: 12px;
        .action { cursor: pointer; &:hover { color: #00aeec; } }
      }
    }
  }
}

.dialog-inner-reply {
  padding-top: 0;
  .el-divider--horizontal { margin: 12px 0; }
}
</style>
